import { useState } from "react";
import { useItems, useCreateItem, useUpdateItem } from "../hooks/useApi";

interface Item {
  id: string;
  code: string | null;
  name: string;
  packSize: string | null;
  caseSize: string | null;
  unitPrice: string | null;
  section: "DAILY" | "MONTHLY";
  sortOrder: number;
  active: boolean;
}

interface ItemForm {
  code: string;
  name: string;
  packSize: string;
  caseSize: string;
  unitPrice: string;
  section: "DAILY" | "MONTHLY";
  sortOrder: string;
}

const emptyForm: ItemForm = {
  code: "",
  name: "",
  packSize: "",
  caseSize: "",
  unitPrice: "",
  section: "DAILY",
  sortOrder: "",
};

function formFromItem(item: Item): ItemForm {
  return {
    code: item.code ?? "",
    name: item.name,
    packSize: item.packSize ?? "",
    caseSize: item.caseSize ?? "",
    unitPrice: item.unitPrice ?? "",
    section: item.section,
    sortOrder: String(item.sortOrder),
  };
}

export function AdminCatalog() {
  const { data: items, isLoading } = useItems(true);
  const createItem = useCreateItem();
  const updateItem = useUpdateItem();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ItemForm>(emptyForm);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");

  const allItems = (items as Item[]) || [];

  const startEdit = (item: Item) => {
    setEditingId(item.id);
    setForm(formFromItem(item));
    setShowCreate(false);
    setError("");
  };

  const startCreate = () => {
    setShowCreate(true);
    setEditingId(null);
    const maxSort = allItems.reduce((m, i) => Math.max(m, i.sortOrder), 0);
    setForm({ ...emptyForm, sortOrder: String(maxSort + 1) });
    setError("");
  };

  const cancel = () => {
    setEditingId(null);
    setShowCreate(false);
    setError("");
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setError("");
    try {
      if (editingId) {
        await updateItem.mutateAsync({ itemId: editingId, data: form });
      } else {
        await createItem.mutateAsync(form);
      }
      cancel();
    } catch (err: any) {
      setError(err.response?.data?.error || "Save failed");
    }
  };

  const toggleActive = async (item: Item) => {
    try {
      await updateItem.mutateAsync({
        itemId: item.id,
        data: { active: !item.active },
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Update failed");
    }
  };

  if (isLoading) return <div style={{ padding: "1rem" }}>Loading...</div>;

  const saving = createItem.isPending || updateItem.isPending;

  return (
    <div style={{ padding: "1rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <h1>Catalog</h1>
        <button onClick={startCreate} style={primaryButton}>
          + New item
        </button>
      </div>
      <p style={{ color: "#666", margin: "0.25rem 0 1.25rem" }}>
        {allItems.filter((i) => i.active).length} active /{" "}
        {allItems.length} total items. Every store counts against this shared
        catalog.
      </p>

      {error && (
        <div
          style={{
            backgroundColor: "#fee",
            color: "#c33",
            padding: "0.75rem",
            borderRadius: "4px",
            marginBottom: "1rem",
          }}
        >
          {error}
        </div>
      )}

      {showCreate && (
        <ItemEditor
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          title="New item"
        />
      )}

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "4px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
          <thead style={{ backgroundColor: "#f8f9fa" }}>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Section</th>
              <th style={thNumStyle}>Pack</th>
              <th style={thNumStyle}>Case</th>
              <th style={thNumStyle}>Unit Price</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {allItems.map((item) =>
              editingId === item.id ? (
                <tr key={item.id}>
                  <td colSpan={9} style={{ padding: 0 }}>
                    <ItemEditor
                      form={form}
                      setForm={setForm}
                      onSave={save}
                      onCancel={cancel}
                      saving={saving}
                      title={`Edit — ${item.name}`}
                    />
                  </td>
                </tr>
              ) : (
                <tr
                  key={item.id}
                  style={{
                    borderBottom: "1px solid #eee",
                    opacity: item.active ? 1 : 0.5,
                  }}
                >
                  <td style={tdStyle}>{item.sortOrder}</td>
                  <td style={tdStyle}>{item.code ?? "–"}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{item.name}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.15rem 0.5rem",
                        borderRadius: "10px",
                        backgroundColor:
                          item.section === "DAILY" ? "#e7f1ff" : "#fff3e0",
                        color: item.section === "DAILY" ? "#0056b3" : "#a06000",
                      }}
                    >
                      {item.section}
                    </span>
                  </td>
                  <td style={tdNumStyle}>{item.packSize ?? "–"}</td>
                  <td style={tdNumStyle}>{item.caseSize ?? "–"}</td>
                  <td style={tdNumStyle}>
                    {item.unitPrice ? `R ${parseFloat(item.unitPrice).toFixed(2)}` : "–"}
                  </td>
                  <td style={tdStyle}>{item.active ? "Active" : "Inactive"}</td>
                  <td style={tdStyle}>
                    <button onClick={() => startEdit(item)} style={smallButton}>
                      Edit
                    </button>{" "}
                    <button
                      onClick={() => toggleActive(item)}
                      style={{
                        ...smallButton,
                        color: item.active ? "#c33" : "#282",
                      }}
                    >
                      {item.active ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ItemEditor({
  form,
  setForm,
  onSave,
  onCancel,
  saving,
  title,
}: {
  form: ItemForm;
  setForm: (f: ItemForm) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  title: string;
}) {
  const field = (label: string, key: keyof ItemForm, props: any = {}) => (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <span style={{ fontSize: "0.75rem", color: "#666" }}>{label}</span>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{ padding: "0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
        {...props}
      />
    </label>
  );

  return (
    <div
      style={{
        border: "1px solid #007bff33",
        backgroundColor: "#f5f9ff",
        borderRadius: "4px",
        padding: "1rem",
        marginBottom: "1rem",
      }}
    >
      <strong style={{ display: "block", marginBottom: "0.75rem" }}>{title}</strong>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: "0.75rem",
          marginBottom: "0.75rem",
        }}
      >
        {field("Code", "code")}
        {field("Name", "name", { required: true })}
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.75rem", color: "#666" }}>Section</span>
          <select
            value={form.section}
            onChange={(e) =>
              setForm({ ...form, section: e.target.value as "DAILY" | "MONTHLY" })
            }
            style={{ padding: "0.5rem", border: "1px solid #ccc", borderRadius: "4px" }}
          >
            <option value="DAILY">DAILY</option>
            <option value="MONTHLY">MONTHLY</option>
          </select>
        </label>
        {field("Pack size", "packSize", { type: "number", inputMode: "decimal" })}
        {field("Case size", "caseSize", { type: "number", inputMode: "decimal" })}
        {field("Unit price (R)", "unitPrice", { type: "number", inputMode: "decimal" })}
        {field("Sort order", "sortOrder", { type: "number", inputMode: "numeric" })}
      </div>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button onClick={onSave} disabled={saving} style={primaryButton}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} style={secondaryButton}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const primaryButton = {
  padding: "0.5rem 1rem",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const secondaryButton = {
  padding: "0.5rem 1rem",
  backgroundColor: "white",
  color: "#333",
  border: "1px solid #ccc",
  borderRadius: "4px",
  cursor: "pointer",
};

const smallButton = {
  padding: "0.25rem 0.5rem",
  backgroundColor: "transparent",
  color: "#007bff",
  border: "none",
  cursor: "pointer",
  fontSize: "0.8rem",
};

const thStyle = {
  padding: "0.6rem 0.75rem",
  textAlign: "left" as const,
  fontWeight: "bold" as const,
  borderBottom: "2px solid #ddd",
};

const thNumStyle = { ...thStyle, textAlign: "right" as const };

const tdStyle = {
  padding: "0.6rem 0.75rem",
  borderBottom: "1px solid #eee",
};

const tdNumStyle = { ...tdStyle, textAlign: "right" as const };
